import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { FormattedMessage, useIntl } from 'react-intl';
import { Box } from '@osmosis/react-ui/components/Box';

import { useQuizContext } from '../../../context/QuizContext';
import { Alert } from '@osmosis/tailwind-ui/components/Alert';
import { Button } from '@osmosis/react-ui/components/Button';
import { Checkbox } from '@osmosis/react-ui/components/Checkbox';
import { P } from '@osmosis/react-ui/components/Text';
import Divider from '../../Divider';
import ActionBar from '../../ModalFooter';
import HeadingBar from '../../ModalHeader';
import { useFetch } from '../../../context/FetchProvider';
import { SelectFieldsBox, SelectHeader } from './RepetitionSettingsFields';
import { RepetitionSettingsFormI } from './types';
import { updateRepetitionSettings } from './api';
import { SPACED_REPETITION_LINK } from '../../../constants';

export interface PropsI {
  initialData: RepetitionSettingsFormI;
  refetch: () => void;
}

/**
 * RepetitionSettingsForm component.
 */
export const RepetitionSettingsForm = ({ initialData, refetch }: PropsI) => {
  const { setShowRepetitionSettings } = useQuizContext();
  const { post } = useFetch();
  const intl = useIntl();

  const methods = useForm<RepetitionSettingsFormI>({
    defaultValues: initialData,
  });

  const { mutate } = useMutation(updateRepetitionSettings(post), {
    /**
     * Close the modal window and update the settings call if the request was successful.
     */
    onSuccess: () => {
      setShowRepetitionSettings(false);
      refetch();
    },
  });

  const isFormWasChanged = methods.formState.isDirty;

  const showCloseConfirm = () => {
    if (window.confirm('Exit without saving changes?')) {
      setShowRepetitionSettings(false);
    }
  };

  const closeRepetitionSettingsModal = (
    e?: React.MouseEvent<HTMLButtonElement>,
  ) => {
    e?.preventDefault();
    if (isFormWasChanged) {
      showCloseConfirm();
    } else {
      setShowRepetitionSettings(false);
    }
  };

  return (
    <FormProvider {...methods}>
      <Box
        onSubmit={methods.handleSubmit((values) => {
          mutate(values);
        })}
        as="form"
        sx={{
          height: '100%',
          display: 'grid',
          gridTemplateRows: '60px auto 70px',
        }}
      >
        <HeadingBar
          onClose={closeRepetitionSettingsModal}
          text={intl.formatMessage({
            id: 'repetitionSettingsForm.heading',
            defaultMessage: 'Spaced repetition settings',
          })}
        />

        <Box sx={{ overflowY: 'auto', padding: '20px' }}>
          <Alert cn="mb-5">
            <FormattedMessage
              id="repetitionSettingsForm.banner"
              defaultMessage='When set to "Auto", Osmosis optimizes your <a>spaced repetition</a> time based on your confidence and responses.'
              values={{
                /**
                 * Chunk for add link to string.
                 */
                a: (chunks) => (
                  <a
                    key="a"
                    target="_blank"
                    href={SPACED_REPETITION_LINK}
                    rel="noreferrer"
                  >
                    {chunks}
                  </a>
                ),
              }}
            >
              {(message) => (
                <P>
                  <b>{message}</b>
                </P>
              )}
            </FormattedMessage>
          </Alert>
          <SelectHeader
            color="positive"
            text={intl.formatMessage({
              id: 'repetitionSettingsForm.header.correct',
              defaultMessage: 'Correct responses',
            })}
          />

          <SelectFieldsBox type="correct" />
          <SelectHeader
            color="negative"
            text={intl.formatMessage({
              id: 'repetitionSettingsForm.header.incorrect',
              defaultMessage: 'Incorrect responses',
            })}
          />

          <SelectFieldsBox type="incorrect" />
          <Divider cn="h-0.5 mb-4" />
          <Checkbox
            {...methods.register('flaggedOnly')}
            label={intl.formatMessage({
              id: 'repetitionSettingsForm.flaggedOnly',
              defaultMessage: 'Only repeat flagged items',
            })}
            data-testid="repeat-flagged-items-checkbox"
          />
        </Box>
        <ActionBar>
          <Button
            onClick={closeRepetitionSettingsModal}
            variant="outline"
            data-testid="cancel-repetition-settings-btn"
          >
            <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
          </Button>
          <Button type="submit" data-testid="apply-repetition-settings-btn">
            <FormattedMessage
              id="repetitionSettingsForm.footer.apply"
              defaultMessage="Apply"
            />
          </Button>
        </ActionBar>
      </Box>
    </FormProvider>
  );
};
